import React, { useEffect, useState, useMemo } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "../ui/Carousel";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "../ui/Card";

import SearchIcon from "../../media/SearchIcon";
import { useTheme } from "../../contexts/ThemeContext";
import { Link } from "react-router-dom";
import useBlogStorage from "../hooks/useBlogStorage";

interface BlogSummary {
  filename: string;
  title: string;
  image?: string;
  summary: string;
}

const BlogPage = () => {
    const { theme } = useTheme();
    const [blogs, setBlogs] = useState<BlogSummary[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);

    const { listMarkdowns: listMarkdownFiles, fetchMarkdown: fetchMarkdownFile } = useBlogStorage();

    // Helper to parse markdown for title, image and summary
    const parseMarkdown = (md: string): { title: string; image?: string; summary: string } => {
        let title = "";
        let image;
        let summary = "";

        const titleMatch = md.match(/^#\s+(.*)/m);
        if (titleMatch) {
            title = titleMatch[1];
        }

        const imageMatch = md.match(/!\[[^\]]*\]\((.*?)\)/);
        if (imageMatch) {
            image = imageMatch[1];
        }

        // get first non-header paragraph as summary
        const paragraphs = md
            .split(/\r?\n\r?\n/) // split on blank lines
            .map((p) => p.trim())
            .filter((p) => p.length > 0 && !p.startsWith("#"));
        if (paragraphs.length) {
            summary = paragraphs[0].replace(/(!?\[[^\]]*\]\([^)]*\))/g, "").slice(0, 250);
        }

        return { title, image, summary };
    };

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const filenames = await listMarkdownFiles();
                // Filter out nulls from filenames before mapping
                const validFilenames = filenames.filter((filename): filename is string => typeof filename === 'string');
                const blogPromises = validFilenames.map(async (filename) => {
                    try {
                        const mdText = await fetchMarkdownFile(filename);
                        const { title, image, summary } = parseMarkdown(mdText);
                        return { filename, title, image, summary } as BlogSummary;
                    } catch (err) {
                        console.error("Error fetching blog", filename, err);
                        return null;
                    }
                });
                const blogResults = await Promise.all(blogPromises);
                const validBlogs = blogResults.filter((b): b is BlogSummary => Boolean(b));
                setBlogs(validBlogs);
                setLoading(false);
            } catch (err) {
                console.error(err);
                setError(true);
                setLoading(false);
            }
        };
        fetchBlogs();
    }, [listMarkdownFiles, fetchMarkdownFile]);

    const carouselSlides = useMemo(() => blogs.slice(0, 4), [blogs]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen text-[#535353] dark:text-[#CEE0EF]">Loading blogs...</div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-screen text-red-500">Error loading blogs.</div>
        );
    }

    return (
        <div className="max-h-screen overflow-y-auto bg-gray-200 dark:bg-gray-900 text-[#535353] dark:text-[#CEE0EF]">
            <div className="sticky z-20 top-0 w-full flex justify-start bg-gray-200 dark:bg-gray-900 py-4 shadow-md transition-all duration-300">
                <div className="scale-90 w-full max-w-xl flex items-center bg-white dark:bg-gray-800 rounded-md px-3.5 py-3">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            placeholder="Search for articles and blogs..."
                            className="w-full h-[32px] pl-10 pr-2 rounded-[3px] border border-[#C6C5C5] dark:border-[#4A5568] bg-transparent text-[#535353] dark:text-[#CEE0EF] text-[15px] focus:outline-none placeholder:text-[#C6C5C5] dark:placeholder:text-[#7A8690]"
                        />
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <SearchIcon theme={theme} />
                        </div>
                    </div>
                    <button className="ml-1 px-3 h-[32px] rounded-[3px] bg-accent1 hover:bg-accent1/90 text-white text-[15px] transition-colors font-semibold">
                        Search
                    </button>
                </div>
            </div>

            <div className="relative w-full flex justify-center items-center">
                <div className="w-full">
                    <Carousel opts={{ loop: true }}>
                        <CarouselContent>
                        {carouselSlides.map((slide, idx) => (
                            <CarouselItem key={idx}>
                            <div className="relative w-full h-[420px] md:h-[520px] overflow-hidden flex items-center justify-start">
                                <img
                                    src={slide.image}
                                    alt="carousel background"
                                    className="absolute w-full h-full min-h-[420px] md:min-h-[520px] object-cover object-center z-0"
                                />
                                <CarouselPrevious className="left-4 top-1/2 -translate-y-1/2" />
                                <CarouselNext className="right-4 top-1/2 -translate-y-1/2" />
                                <div className="flex items-start w-full max-w-2xl z-10 mt-32 ml-16">
                                    <Card className="bg-white dark:bg-gray-900 shadow-xl p-8 border-0 w-full max-w-2xl">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-xl font-bold text-accent1 dark:text-white mb-2">
                                                {slide.title}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="mb-2 overflow-hidden">
                                            <CardDescription className="text-md text-[#535353] dark:text-[#CEE0EF] line-clamp-3">
                                                {slide.summary}
                                            </CardDescription>
                                        </CardContent>
                                        <CardFooter>
                                            <Link to={`/blog/${slide.filename.replace('.md', '')}`} className="text-sm px-5 py-1.5 rounded bg-accent1 text-white font-semibold shadow hover:bg-accent1/90 transition-colors">
                                                Read
                                            </Link>
                                        </CardFooter>
                                    </Card>
                                </div>
                            </div>
                            </CarouselItem>
                        ))}
                        </CarouselContent>
                    </Carousel>
                </div>
            </div>

            <div className="flex justify-center items-center">
                <div className="w-full max-w-6xl px-4 gap-12 my-16 place-items-center grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 ">
                    {blogs.map((blog, idx) => (
                    <Link key={idx} to={`/blog/${blog.filename.replace('.md', '')}`} className="block">
                        <Card className="flex flex-col h-[420px] shadow-lg overflow-hidden border-0 bg-white dark:bg-[#2D3748]">
                            <div className="h-[200px] overflow-hidden">
                                <img src={blog.image} alt="blog" className="min-h-[200px]" />
                            </div>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-xl font-semibold text-accent1 dark:text-[#CEE0EF] line-clamp-2">
                                    {blog.title}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="flex-1 pb-2 overflow-hidden">
                                <CardDescription className="text-sm text-[#535353] dark:text-[#CEE0EF] line-clamp-3">
                                    {blog.summary}
                                </CardDescription>
                            </CardContent>
                            <CardFooter className="flex items-center justify-between text-xs text-[#7A8690] dark:text-[#A0AEC0]">
                                <span>Oct 29, 2024</span>
                                <span>2 min read</span>
                            </CardFooter>
                        </Card>
                    </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BlogPage;
